// src/pages/auth/ResetPassword.js - set a new password from the e-mail link.
// Audit FL-47: the link is checked before the form is shown (no flash), the password
// needs at least 8 characters, the button shows progress, and an expired link offers
// to request a new one.
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, CheckCircle } from 'lucide-react';
import api, { getErrorMessage } from '../../services/api';
import { Alert, Button, Spinner } from '../../components/ui';
import PasswordField from '../../components/leader/PasswordField';

const MIN_PASSWORD = 8;

const Shell = ({ title, children }) => (
  <div className="flex min-h-screen flex-col justify-center bg-gray-100 px-4 py-12 sm:px-6 lg:px-8">
    <div className="sm:mx-auto sm:w-full sm:max-w-md">
      <p className="text-center text-2xl font-bold text-indigo-700">Best of the Best</p>
      {title && <h1 className="mt-2 text-center text-xl font-semibold text-gray-900">{title}</h1>}
    </div>
    <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
      <div className="rounded-lg bg-white px-4 py-8 shadow sm:px-10">{children}</div>
    </div>
  </div>
);

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState('checking'); // checking | valid | invalid | done
  const [purpose, setPurpose] = useState('reset'); // reset (e-mail) | setup (link from the organiser)
  const [checkError, setCheckError] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setState('checking');
    setCheckError(null);
    api
      .get(`/auth/validate-reset-token/${encodeURIComponent(token)}`)
      .then((res) => {
        if (cancelled) return;
        setState(res.data && res.data.valid ? 'valid' : 'invalid');
        if (res.data && res.data.purpose) setPurpose(res.data.purpose);
      })
      .catch((err) => {
        if (cancelled) return;
        // A network problem is not an invalid link - let the user try the form anyway.
        if (err && err.status) setState('invalid');
        else {
          setCheckError(getErrorMessage(err));
          setState('valid');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (state !== 'done') return undefined;
    const timer = setTimeout(() => navigate('/login', { replace: true }), 4000);
    return () => clearTimeout(timer);
  }, [state, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < MIN_PASSWORD) {
      setError(`Heslo musí mít alespoň ${MIN_PASSWORD} znaků.`);
      return;
    }
    if (password !== confirmPassword) {
      setError('Hesla se neshodují.');
      return;
    }
    setIsSubmitting(true);
    try {
      await api.post(`/auth/reset-password/${encodeURIComponent(token)}`, { password });
      setState('done');
    } catch (err) {
      setError(getErrorMessage(err, 'Heslo se nepodařilo změnit. Zkuste to prosím znovu.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (state === 'checking') {
    return (
      <Shell>
        <Spinner label="Ověřuji odkaz…" />
      </Shell>
    );
  }

  if (state === 'invalid') {
    return (
      <Shell title="Odkaz už neplatí">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-500" aria-hidden="true" />
          <p className="mt-3 text-sm text-gray-700">
            Odkaz pro obnovení hesla platí 30 minut (odkaz od organizátora 7 dní) a lze ho použít jen jednou. Požádejte
            prosím o nový.
          </p>
          <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-center">
            <Button onClick={() => navigate('/login', { state: { forgotPassword: true } })}>Poslat nový odkaz</Button>
            <Button variant="secondary" onClick={() => navigate('/login')}>
              Zpět na přihlášení
            </Button>
          </div>
        </div>
      </Shell>
    );
  }

  if (state === 'done') {
    return (
      <Shell title="Heslo je změněné">
        <div className="text-center">
          <CheckCircle className="mx-auto h-12 w-12 text-green-600" aria-hidden="true" />
          <p className="mt-3 text-sm text-gray-700">Za chvíli vás přesměrujeme na přihlášení.</p>
          <Link to="/login" className="mt-4 inline-block font-medium text-indigo-700 hover:text-indigo-900">
            Přihlásit se hned
          </Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell title={purpose === 'setup' ? 'Nastavte si heslo k účtu skupiny' : 'Nastavení nového hesla'}>
      {purpose === 'setup' && (
        <p className="mb-4 text-sm text-gray-700">
          Tento odkaz vám poslal organizátor. Zvolte si nové heslo – původní heslo přestane platit a pak se přihlásíte
          svým e-mailem a novým heslem.
        </p>
      )}
      {checkError && (
        <Alert tone="warning" className="mb-4">
          {checkError}
        </Alert>
      )}
      <form className="space-y-5" onSubmit={handleSubmit} noValidate>
        <PasswordField
          id="password"
          label="Nové heslo"
          name="new-password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          hint={`Alespoň ${MIN_PASSWORD} znaků.`}
          required
        />
        <PasswordField
          id="confirmPassword"
          label="Nové heslo znovu"
          name="confirm-password"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          error={confirmPassword && password !== confirmPassword ? 'Hesla se neshodují.' : null}
          required
        />
        {error && <Alert tone="error">{error}</Alert>}
        <Button type="submit" className="w-full" loading={isSubmitting}>
          {isSubmitting ? 'Ukládám…' : 'Nastavit nové heslo'}
        </Button>
      </form>
    </Shell>
  );
};

export default ResetPassword;
